import ErrorPopup from '@components/ErrorPopup'
import Picker from '@components/Picker'
import Prompt from '@components/Prompt'
import useConfig from '@plug-core/hooks/useConfig'
import { CanisterUpdateDetails } from '@plug-core/hooks/useTopup'
import { CanisterInfo } from '@plug-core/idls/topUp/topUp'
import { logger } from '@plug-core/logger'
import { validateCanisterId } from '@plug-core/utils/validationHelper'
import { Button, Field, Heading, Input, Switch, Text } from '@ui'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiInformationCircle } from 'react-icons/hi'

interface Props {
  hideSelf: () => void
  addCanister: (canister: CanisterUpdateDetails) => Promise<boolean>
  updateCanister?: (canister: CanisterUpdateDetails) => Promise<boolean>
  newCanister: boolean
  canister?: CanisterInfo
  prefilledCanisterId?: string
}

type TopupMode = 'SNS' | 'Blackhole' | 'Manual'

const TOPUP_TYPES: { key: TopupMode; humanValue: string }[] = [
  { key: 'SNS', humanValue: 'SNS' },
  { key: 'Blackhole', humanValue: 'Blackhole' },
  { key: 'Manual', humanValue: 'Manual' },
]

const CanisterForm: FC<Props> = ({
  hideSelf,
  addCanister,
  updateCanister,
  newCanister,
  canister,
  prefilledCanisterId,
}) => {
  const [submitLoading, setSubmitLoading] = useState(false)

  const [canisterName, setCanisterName] = useState<string>('')
  const [canisterId, setCanisterId] = useState<string>('')
  const [topupAmount, setTopupAmount] = useState<string>('')
  const [minCycles, setMinCyles] = useState<string>('')
  const [autoTopup, setAutoTopup] = useState<boolean>(false)
  const [snsRoot, setSnsRoot] = useState<string>('')
  const [saveError, setSaveError] = useState<string>()
  const [topupMode, setTopupMode] = useState<TopupMode>('Manual')
  const [prompt, setPrompt] = useState<{
    title: string
    description: string
  } | null>(null)

  const { t } = useTranslation()
  const { activeSns } = useConfig()

  useEffect(() => {
    if (!canister) return

    setCanisterName(canister.name)
    setCanisterId(canister.id.toString())
    setTopupAmount(convertToTC(canister.topup_amount))
    setMinCyles(convertToTC(canister.min_cycles))
    setAutoTopup(canister.auto_topup)
    setTopupMode(Object.keys(canister.topup_type)[0] as TopupMode)
    setSnsRoot(canister.sns_root[0]?.toString() || '')
  }, [canister])

  useEffect(() => {
    prefilledCanisterId && setCanisterId(prefilledCanisterId)
  }, [prefilledCanisterId])

  const convertToTC = (amount: BigInt) => {
    return Number(amount) === 0 ? '' : (Number(amount) * 10 ** -12).toString()
  }

  const handleSubmit = () => {
    if (!canisterId) return

    setSubmitLoading(true)

    const method = newCanister ? addCanister : updateCanister

    method?.({
      canisterId,
      name: canisterName,
      autoTopup,
      topupAmount,
      topupThreshold: minCycles,
      topupMode,
      snsRoot,
    })
      .then(() => {
        hideSelf()
      })
      .catch((e) => {
        logger.debug(e)
        setSaveError(t(`topup.saveError.${Object.keys(e)[0]}`))
      })
      .finally(() => setSubmitLoading(false))
  }

  const onInfoClick = (event: any, topupMode: TopupMode) => {
    event.stopPropagation()

    setPrompt({
      title: t(`topup.${topupMode}.title`),
      description: t(`topup.${topupMode}.description`),
    })
  }

  const snsPickerValues = useMemo(
    () =>
      activeSns.map((sns) => ({
        key: sns.rootCanisterId || '',
        humanValue: sns.symbol,
      })),
    [activeSns]
  )

  return (
    <div className="mb-3 flex flex-col gap-3">
      <Heading level={4} align="center">
        {newCanister ? 'Add Canister' : 'Update Canister Settings'}
      </Heading>

      <Field error={!!canisterId && !validateCanisterId(canisterId)}>
        <Input
          mono
          aria-label={t('topup.canisterForm.canisterId')}
          placeholder={t('topup.canisterForm.canisterId')}
          value={canisterId}
          onChange={(e) => setCanisterId(e.target.value)}
        />
      </Field>

      <Field>
        <Input
          aria-label={t('topup.canisterForm.name')}
          placeholder={t('topup.canisterForm.name')}
          value={canisterName}
          onChange={(e) => setCanisterName(e.target.value)}
        />
      </Field>

      <div className="relative flex items-center">
        <div className="flex flex-1 flex-col">
          <Picker
            values={TOPUP_TYPES}
            currentValue={topupMode}
            onValueChange={(newValue) => setTopupMode(newValue as TopupMode)}
            heading={t('topup.canisterForm.topupMode')}
          />
        </div>

        <button
          type="button"
          aria-label={t(`topup.${topupMode}.title`)}
          className="absolute top-1/2 right-11 grid -translate-y-1/2 cursor-pointer place-items-center border-0 bg-transparent p-0 text-[20px] text-fg-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
          onClick={(e) => onInfoClick(e, topupMode)}
        >
          <HiInformationCircle aria-hidden="true" />
        </button>
      </div>

      {topupMode === 'SNS' && (
        <Picker
          values={snsPickerValues}
          currentValue={snsRoot}
          onValueChange={setSnsRoot}
          heading={t('topup.canisterForm.sns')}
        />
      )}

      {topupMode !== 'Manual' && (
        <>
          <Switch
            checked={autoTopup}
            onCheckedChange={(checked) => setAutoTopup(checked)}
            label={t('topup.canisterForm.autoTopup')}
            className="py-1"
          />

          {autoTopup && (
            <>
              <Field>
                <Input
                  aria-label={t('topup.canisterForm.topupAmount')}
                  placeholder={t('topup.canisterForm.topupAmount')}
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(e.target.value)}
                  className="tabular"
                  trailing="TC"
                />
              </Field>
              <Field>
                <Input
                  aria-label={t('topup.canisterForm.topupThreshold')}
                  placeholder={t('topup.canisterForm.topupThreshold')}
                  value={minCycles}
                  onChange={(e) => setMinCyles(e.target.value)}
                  className="tabular"
                  trailing="TC"
                />
              </Field>
            </>
          )}
        </>
      )}

      <Button variant="primary" loading={submitLoading} fullWidth onClick={handleSubmit}>
        {newCanister ? t('common.save') : t('common.update')}
      </Button>

      <ErrorPopup
        title="Save Error"
        description={saveError}
        visible={!!saveError}
        onDismiss={() => setSaveError(undefined)}
      />

      <Prompt show={!!prompt} close={() => setPrompt(null)}>
        <div className="flex w-full flex-col items-center">
          <div className="mb-1 flex flex-col items-center">
            <Heading level={4} className="mt-1">
              {prompt?.title}
            </Heading>
            <Text align="center" tone="fg-2" className="mt-2 mb-4">
              {prompt?.description}
            </Text>
          </div>

          <Button onClick={() => setPrompt(null)} variant="secondary">
            {t('common.understood')}
          </Button>
        </div>
      </Prompt>
    </div>
  )
}

export default CanisterForm
